import React from 'react';
import { getPasswordStrength, PASSWORD_REQUIREMENTS } from '../../utils/passwordValidation';

const PasswordStrengthIndicator = ({ password, showRequirements = true }) => {
  const analysis = getPasswordStrength(password);
  
  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'strong':
        return 'bg-signal-green-500';
      case 'medium':
        return 'bg-signal-amber-500';
      default:
        return 'bg-signal-red-500';
    }
  };

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'strong':
        return 'Strong';
      case 'medium':
        return 'Medium';
      default:
        return 'Weak';
    }
  };

  const requirements = [
    {
      key: 'length',
      text: `At least ${PASSWORD_REQUIREMENTS.minLength} characters`,
      met: analysis.requirements.length
    },
    {
      key: 'uppercase',
      text: 'One uppercase letter (A-Z)',
      met: analysis.requirements.uppercase
    },
    {
      key: 'lowercase', 
      text: 'One lowercase letter (a-z)',
      met: analysis.requirements.lowercase
    },
    {
      key: 'numbers',
      text: 'One number (0-9)',
      met: analysis.requirements.numbers
    },
    {
      key: 'special',
      text: 'One special character (!@#$%^&*)',
      met: analysis.requirements.special
    }
  ];

  if (!password) {
    return null;
  }

  return (
    <div className="mt-2">
      {/* Strength Bar */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-ink-700">Password strength:</span>
        <span className={`text-sm font-medium ${
          analysis.strength === 'strong' ? 'text-signal-green-600' :
          analysis.strength === 'medium' ? 'text-signal-amber-600' : 'text-signal-red-600'
        }`}>
          {getStrengthText(analysis.strength)}
        </span>
      </div>
      
      <div className="w-full bg-cream-300 rounded-full h-2 mb-3">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(analysis.strength)}`}
          style={{ width: `${analysis.score * 100}%` }}
        />
      </div>

      {/* Requirements List */}
      {showRequirements && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-ink-700 mb-2">Password must contain:</p>
          {requirements.map((req) => (
            <div key={req.key} className="flex items-center space-x-2">
              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                req.met ? 'bg-signal-green-100' : 'bg-cream-200'
              }`}>
                {req.met ? (
                  <svg className="w-3 h-3 text-signal-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <div className="w-2 h-2 bg-ink-300 rounded-full" />
                )}
              </div>
              <span className={`text-sm ${req.met ? 'text-signal-green-600' : 'text-ink-500'}`}>
                {req.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;